import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { getAllTemperatureOptions, groupTemperatureByCenter, transformToSelectOptions } from '~/utils';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeApiResponse, TypeGet3plFilterAllResponse, TypeReceiveFilterOptions } from '~/types/receive.types';

type ScreenName =
  | 'RECEIVING_REQUEST' // 입고요청관리
  | 'ADD_RECEIVING_GOODS' // 입고상품추가
  | 'RECEIVING_PROGRESS' // 입고 현황
  | 'REPORT_HISTORY' // 입고 리포트 관리
  | 'INBOUND_REQUEST_PROGRESS'; // 3PL 입고 현황

/**
 * 필터 옵션 응답 데이터를 SelectOption 형태로 변환
 */
const formatReceiveFilterAllResponse = (data: TypeGet3plFilterAllResponse): TypeReceiveFilterOptions => {
  const groupedTemperatureByCenter = groupTemperatureByCenter(data.clusters, data.warehouses);

  const center = transformToSelectOptions(data.clusters, 'clusterCode', 'clusterName');
  const temperature = getAllTemperatureOptions(groupedTemperatureByCenter);
  const status = transformToSelectOptions(data.statuses, 'statusCode', 'statusName');
  const releaseCode = transformToSelectOptions(data.releaseCodes, 'releaseCode', 'releaseName');
  const keywordType = transformToSelectOptions(data.codeTypes, 'type', 'name');
  const dateType = transformToSelectOptions(data.dateTypes, 'type', 'name');
  const xDock = transformToSelectOptions(data.xdockAllYesNo, 'code', 'name');
  const disallow = transformToSelectOptions(data.disallowAllYesNo, 'code', 'name');
  const supplierTextFormatter = (value: string, text: string) => {
    return `${text}${value ? ' (' + value + ')' : ''}`;
  };
  const supplier = transformToSelectOptions(data.suppliers, 'code', 'name', supplierTextFormatter);
  const vehicleSize = transformToSelectOptions(data.vehicleSizes, 'code', 'name');
  const reportTaskCodes = transformToSelectOptions(data.reportTaskCodes, 'code', 'name');
  const reportStatus = transformToSelectOptions(data.reportStatuses, 'code', 'name');
  const reportAllYesNo = transformToSelectOptions(data.reportAllYesNo, 'code', 'name');

  return {
    groupedTemperatureByCenter,
    center,
    temperature,
    status,
    releaseCode,
    keywordType,
    dateType,
    xDock,
    disallow,
    supplier,
    vehicleSize,
    reportTaskCodes,
    reportStatus,
    reportAllYesNo,
  };
};

/**
 * [입고관리] 필터 옵션 조회 API
 */
export const fetchFilterAll = async (screenName: ScreenName): Promise<TypeReceiveFilterOptions> => {
  const { data } = await axiosInstance.get<TypeApiResponse<TypeGet3plFilterAllResponse>>(
    `${API_ENDPOINT.RECEIVE.GET_FILTER_ALL}?screenName=${screenName}`
  );
  return formatReceiveFilterAllResponse(data.data);
};

/**
 * [입고관리] 필터 옵션 조회 Query Hook
 */
export const useGetFilterAllQuery = (screenName: ScreenName, options?: { enabled?: boolean; silent?: boolean }) => {
  return useQuery({
    queryKey: receiveKeys.filterAll(screenName),
    queryFn: () => fetchFilterAll(screenName),
    staleTime: 1000 * 60 * 30, // 30분
    enabled: options?.enabled ?? true,
    meta: options?.silent ? { silent: true } : undefined,
  });
};
